import React from "react"
import { Box, Divider, mq, Text, theme } from "@pallet-hq/hegel"
import CheckLabel from "@pallet/components/CheckLabel"
import NextLink from "components/Link/NextLink"
import { ContingencySalesButton } from "@pallet/features/ApplylistType/components/ContingencyTierCta"
import { ContingencyPlanBanner_applylist } from "__generated__/ContingencyPlanBanner_applylist"
import styled from "@emotion/styled"

const CONTINGENCY_PLAN_DETAILS = {
    name: "Boutique",
    description: "A full-service hiring solution, custom to your needs.",
    percentage: "15%",
    features: [
        "Access to talent database",
        "Custom outreach to my network",
        "Dedicated sourcing support",
    ],
}

type ContingencyPlanBannerProps = {
    applylist: ContingencyPlanBanner_applylist
}

const ContingencyPlanBanner = ({ applylist }: ContingencyPlanBannerProps) => {
    const baseHref = "/hire"
    return (
        <ContingencyBlock>
            <Box width="100%" p="large" style={{ boxSizing: "border-box" }}>
                <Text variant="titleMedium" mb="small">
                    {CONTINGENCY_PLAN_DETAILS.name}
                </Text>
                <Text variant="headlineSmall" color="gray600">
                    {CONTINGENCY_PLAN_DETAILS.description}
                </Text>
                <Divider my="medium" />
                <Text variant="uiSmall500" color="gray600" mb="small">
                    Features
                </Text>
                <Bullets>
                    {CONTINGENCY_PLAN_DETAILS.features.map((feature, index) => (
                        <CheckLabel
                            key={index}
                            label={feature}
                            enabled={true}
                            textColor={theme.colors.gray900}
                            iconColor={theme.colors.gray900}
                        />
                    ))}
                </Bullets>
            </Box>
            <RightBlock>
                <Text variant="titleLarge" mb="xxsmall">
                    {CONTINGENCY_PLAN_DETAILS.percentage}
                </Text>
                <Text variant="headlineSmall" color="gray600">
                    of first-year salary
                </Text>
                <Text variant="uiSmall600" color="gray500" mb="medium">
                    (Paid upon hire)
                </Text>
                <ContingencySalesButton
                    applylist={applylist}
                    variant="primary"
                    size="small"
                    bg="gray700"
                    color="white"
                    mb="small"
                />
                <div>
                    <NextLink href={baseHref} passHref>
                        <Text as="a" variant="uiSmall500" color="gray900">
                            Other subscription options
                        </Text>
                    </NextLink>
                </div>
            </RightBlock>
        </ContingencyBlock>
    )
}

export default ContingencyPlanBanner

const ContingencyBlock = styled.div`
    background: ${theme.colors.white};
    max-width: 1000px;
    margin: 0 auto;
    display: flex;
    border-radius: 16px;
    box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.16);

    ${mq({
        flexDirection: ["column", "row"],
        justifyContent: ["space-between"],
    })};
`

const RightBlock = styled.div`
    background: ${theme.colors.gray100};
    padding: ${theme.space.large};
    text-align: center;
    white-space: nowrap;
    ${mq({ borderRadius: ["0 0 16px 16px", "0 16px 16px 0"] })};
`

const Bullets = styled.div`
    display: grid;
    column-gap: ${theme.space.medium};
    row-gap: ${theme.space.xxsmall};

    ${mq({
        gridTemplateColumns: ["none", "repeat(2, 1fr)"],
    })}
`
